/**
 * Shows the price and struck-through compare price of the selected Kaching Bundles deal in the
 * add to cart button.
 *
 * The prices are exposed as the `--atc-bundle-price` and `--atc-bundle-compare-price` custom
 * properties on the document root and rendered via CSS (`.add-to-cart-button--bundle-price::after`
 * and `::before`). Because they live outside the button markup, they survive the morphs the theme
 * applies to the buy buttons on variant change.
 *
 * Kaching docs: https://support.kachingappz.com/en/articles/8609400
 */

const PRICE_PROPERTY = '--atc-bundle-price';
const COMPARE_PRICE_PROPERTY = '--atc-bundle-compare-price';
const SELECTED_BAR_SELECTOR = '.kaching-bundles__bar--selected, .kaching-bundles__bar[data-selected="true"]';
const BAR_PRICE_SELECTOR = '.kaching-bundles__bar-price';
const BAR_COMPARE_PRICE_SELECTOR = '.kaching-bundles__bar-full-price';
const BUNDLE_EVENTS = ['variants-changed', 'deal-bar-selected', 'variant-selected'];

/** @type {WeakSet<Element>} */
const boundBlocks = new WeakSet();

/**
 * @param {string} property
 * @param {string | null | undefined} text
 */
function setProperty(property, text) {
  const value = text?.replace(/\s+/g, ' ').trim();

  if (value) {
    document.documentElement.style.setProperty(property, JSON.stringify(value));
  } else {
    document.documentElement.style.removeProperty(property);
  }
}

/**
 * @param {{ price?: string, comparePrice?: string }} prices
 */
function setPrices({ price, comparePrice }) {
  setProperty(PRICE_PROPERTY, price);
  // Only show the compare price when it differs from the price
  setProperty(COMPARE_PRICE_PROPERTY, comparePrice && comparePrice.trim() !== price?.trim() ? comparePrice : undefined);
}

/**
 * Looks for a formatted price string in the Kaching event detail.
 * @param {unknown} detail
 * @returns {string | undefined}
 */
function getPriceFromDetail(detail) {
  if (!detail || typeof detail !== 'object') return;

  for (const [key, value] of Object.entries(detail)) {
    if (/price/i.test(key) && typeof value === 'string' && /\d/.test(value)) return value;
  }
}

/**
 * @param {Element} block
 * @returns {{ price?: string, comparePrice?: string }}
 */
function getPricesFromBlock(block) {
  const root = block.shadowRoot ?? block;
  const bar = root.querySelector(SELECTED_BAR_SELECTOR);

  return {
    price: bar?.querySelector(BAR_PRICE_SELECTOR)?.textContent ?? undefined,
    comparePrice: bar?.querySelector(BAR_COMPARE_PRICE_SELECTOR)?.textContent ?? undefined,
  };
}

/**
 * @param {Element} block
 */
function bindBlock(block) {
  if (boundBlocks.has(block)) return;
  boundBlocks.add(block);

  const update = (/** @type {Event} */ event) => {
    const detailPrice = event instanceof CustomEvent ? getPriceFromDetail(event.detail) : undefined;

    // Kaching updates its own markup after dispatching, so read the selected bar on the next frame.
    requestAnimationFrame(() => {
      const prices = getPricesFromBlock(block);
      setPrices({ ...prices, price: prices.price ?? detailPrice });
    });
  };

  for (const eventName of BUNDLE_EVENTS) block.addEventListener(eventName, update);

  const initialPrices = getPricesFromBlock(block);
  if (initialPrices.price) return setPrices(initialPrices);

  // Wait for Kaching to render its deal bars before reading the preselected deal.
  const observer = new MutationObserver(() => {
    const prices = getPricesFromBlock(block);
    if (!prices.price) return;
    setPrices(prices);
    observer.disconnect();
  });
  observer.observe(block.shadowRoot ?? block, { childList: true, subtree: true, attributes: true });
  setTimeout(() => observer.disconnect(), 15000);
}

function init() {
  const blocks = document.querySelectorAll('kaching-bundles-block');
  blocks.forEach(bindBlock);
  return blocks.length > 0;
}

if (!init()) {
  // The Kaching app embed renders its block asynchronously.
  const observer = new MutationObserver(() => {
    if (init()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 15000);
}
